import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../helpers/language';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import parse from 'html-react-parser';
import { validateJoinTeamForm } from '../../utils/validations';
import { showError, showSuccess } from '../../utils/notify';
import { setLoading } from '../ui/uiSlice';
import { sendRRHH } from '../../hooks/useSendRRHH';
import { useDispatch } from 'react-redux';

const JoinTeamSection = () => {
    const { language } = useLanguage();
    const dispatch = useDispatch();
    const t = translations[language].joinTeam;
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const siteKey: any = process.env.REACT_APP_reCAPTCHA_KEY_SITE;

    const [cvFile, setCvFile] = useState<any>(null);
    const [cvFileName, setCvFileName] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                e.target.value = '';
                setCvFile(null);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result?.toString().split(',')[1] || null;
                setCvFile(base64String);
                setCvFileName(file.name);
            };
            reader.readAsDataURL(file);
        }
    };


    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        message: '',
        cv: cvFile,
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let data = formData;
        if (cvFile) {
            data = { ...formData, cv: cvFile };
        }

        const errors = validateJoinTeamForm(data, t.form.errors);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        dispatch(setLoading(true));
        const token = await recaptchaRef.current?.executeAsync();
        recaptchaRef.current?.reset();

        const send = await sendRRHH(data, language);
        dispatch(setLoading(false));

        if (send?.Result) {
            showSuccess(t.form.success);
            setFormData({
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                message: '',
                cv: '',
            });
            setCvFile(null);
            setCvFileName(null);
        } else {
            showError(t.form.error);
        }
    }

    return (
        <>
            <section id="join-team" className="section-image" data-aos="fade-up">
                <div className="content-image-about">
                    <h1 className="content-about-title">
                        {t.header_part1} <span className="text-span-10">{t.header_part2}</span>
                    </h1>

                </div>
            </section>

            <section className="section-form-join" data-aos="fade-up">
                <div className="section-contact-left">
                    <h1 className="contact-title">{t.subtitle}</h1>
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="input-div-group">
                            <div className="input-div">
                                <label htmlFor="firstName" className="field-label">{t.form.firstName}</label>
                                <input
                                    className="input-form icon-user-black w-input"
                                    type="text"
                                    id="firstName"
                                    placeholder={t.form.firstName}
                                    onChange={handleChange}
                                    value={formData.firstName}
                                />
                                {formErrors.firstName && <small className="error-text">{formErrors.firstName}</small>}
                            </div>
                            <div className="input-div">
                                <label htmlFor="lastName" className="field-label">{t.form.lastName}</label>
                                <input
                                    className="input-form icon-user-black w-input"
                                    type="text"
                                    id="lastName"
                                    placeholder={t.form.lastName}
                                    onChange={handleChange}
                                    value={formData.lastName}
                                />
                                {formErrors.lastName && <small className="error-text">{formErrors.lastName}</small>}
                            </div>
                        </div>

                        <div className="input-div">
                            <label htmlFor="phone" className="field-label">{t.form.phone}</label>
                            <input
                                className="input-form icon-phone-black w-input"
                                type="tel"
                                id="phone"
                                placeholder={t.form.phone}
                                onChange={handleChange}
                                value={formData.phone}
                            />
                            {formErrors.phone && <small className="error-text">{formErrors.phone}</small>}
                        </div>

                        <div className="input-div">
                            <label htmlFor="email" className="field-label">{t.form.email}</label>
                            <input
                                className="input-form icon-mail-black w-input"
                                type="email"
                                id="email"
                                placeholder={t.form.email}
                                onChange={handleChange}
                                value={formData.email}
                            />
                            {formErrors.email && <small className="error-text">{formErrors.email}</small>}
                        </div>

                        <div className="input-div">
                            <label htmlFor="message" className="field-label">{t.form.description}</label>
                            <textarea
                                className="input-form input-area w-input"
                                id="message"
                                placeholder={t.form.description}
                                onChange={handleChange}
                                value={formData.message}
                            />
                            {formErrors.message && <small className="error-text">{formErrors.message}</small>}
                        </div>

                        <div className="input-div">
                            <label htmlFor="cv" className="field-label">{t.form.cv}</label>
                            <button
                                type="button"
                                className="input-form icon-upload-black w-input"
                                onClick={() => {
                                    const input = document.getElementById('cv') as HTMLInputElement;
                                    if (input) input.value = '';
                                    setCvFile(null);
                                    setCvFileName(null);
                                    input?.click();
                                }}
                            >
                                {cvFileName || t.form.cv}
                            </button>
                            <input
                                type="file"
                                id="cv"
                                className="hidden-file-input"
                                onChange={handleFileChange}
                                accept="application/pdf"
                            />
                            {formErrors.cv && <small className="error-text">{formErrors.cv}</small>}
                        </div>

                        <div className="w-form-formrecaptcha recaptcha-custom">
                            {siteKey && (
                                <ReCAPTCHA
                                    sitekey={siteKey}
                                    ref={recaptchaRef}
                                    onChange={(token) => {
                                        console.log('Captcha token:', token);
                                    }}
                                />
                            )}
                        </div>

                        <input type="submit" className="button-form w-button" value={t.form.submit} />
                    </form>

                    <div className="w-form-done"><div>{t.form.success}</div></div>
                    <div className="w-form-fail"><div>{t.form.error}</div></div>
                </div>

                <div className="section-contact-rigth">
                    <div className="div-block-5">
                        <h1 className="heading-4">{parse(t.promo.title)}</h1>
                        <p className="paragraph-2">{parse(t.promo.description)}</p>
                    </div>
                    <img
                        src="/images/apply-image.jpg"
                        alt="apply"
                        className="image-9"
                        srcSet="/images/apply-image-p-500.jpg 500w, /images/apply-image-p-800.jpg 800w, /images/apply-image-p-1080.jpg 1080w, /images/apply-image.jpg 1407w"
                        sizes="(max-width: 1407px) 100%, 1407px"
                    />
                </div>
            </section>
        </>
    );
};

export default JoinTeamSection;
